import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getDashboard } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Landmark, TrendingUp, TrendingDown, Wallet, Users } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>
            S/ {typeof value === 'number' ? value.toLocaleString('es-PE', { minimumFractionDigits: 2 }) : '0.00'}
          </p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { empresaActual, loading } = useApp();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      if (!empresaActual?.id) return;
      
      setLoadingData(true);
      try {
        const res = await getDashboard(empresaActual.id);
        setData(res.data);
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        setLoadingData(false);
      }
    };

    loadDashboard();
  }, [empresaActual?.id]);

  if (loading || loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!empresaActual) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Selecciona una empresa para ver el dashboard</p>
      </div>
    );
  }

  const saldoTotal = data?.saldos_cuentas?.reduce((sum, c) => sum + (c.saldo || 0), 0) || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">{empresaActual.nombre}</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Saldo Total en Cuentas"
          value={saldoTotal}
          icon={Landmark}
          color="text-emerald-600"
          subtitle="Todas las cuentas"
        />
        <StatCard
          title="Ingresos del Mes"
          value={data?.ingresos_periodo || 0}
          icon={TrendingUp}
          color="text-blue-600"
          subtitle="Período actual"
        />
        <StatCard
          title="Egresos del Mes"
          value={data?.egresos_periodo || 0}
          icon={TrendingDown}
          color="text-red-600"
          subtitle="Período actual"
        />
        <StatCard
          title="CxP Pendiente"
          value={data?.cxp_pendiente || 0}
          icon={Wallet}
          color="text-orange-600"
          subtitle="Por pagar"
        />
      </div>

      {/* Saldos por Cuenta */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Landmark className="w-5 h-5 text-emerald-600" />
              Saldos por Cuenta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.saldos_cuentas?.length > 0 ? (
                data.saldos_cuentas.map((cuenta) => (
                  <div key={cuenta.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{cuenta.nombre}</p>
                      <p className="text-xs text-gray-500">{cuenta.moneda}</p>
                    </div>
                    <p className={`font-bold ${cuenta.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {cuenta.moneda === 'USD' ? '$' : 'S/'} {cuenta.saldo?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No hay cuentas registradas</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              Resumen Financiero
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-700">Neto del Período</span>
                <span className={`font-bold ${(data?.neto_caja || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  S/ {(data?.neto_caja || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <span className="text-orange-700">CxP Pendiente</span>
                <span className="font-bold text-orange-600">
                  S/ {(data?.cxp_pendiente || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-green-700">CxC Pendiente</span>
                <span className="font-bold text-green-600">
                  S/ {(data?.cxc_pendiente || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
